import React, { useEffect, useRef, useState } from "react";
import { crm } from "@/lib/crm";
import { formatPrice } from "@/lib/format";
import { Loader2, Tag, TrendingUp, TrendingDown, ChevronDown, Check, Info } from "lucide-react";
import { AreaChart, Area, ResponsiveContainer, Tooltip, YAxis } from "recharts";

const TONE = {
  green: { text: "#16A34A", bg: "rgba(22,163,74,0.12)", dot: "#16A34A", ring: "rgba(22,163,74,0.35)" },
  amber: { text: "#D97706", bg: "rgba(217,119,6,0.12)", dot: "#F59E0B", ring: "rgba(245,158,11,0.35)" },
  red: { text: "#E11D48", bg: "rgba(225,29,72,0.12)", dot: "#E11D48", ring: "rgba(225,29,72,0.35)" },
};

const pct = (x, low, high) => (high > low ? Math.max(0, Math.min(100, ((x - low) / (high - low)) * 100)) : 50);

// Legacy external deal-score badge — only shown when PMI has no market data.
function LegacyBadge({ deal, t }) {
  const d = Math.round(deal.delta_pct);
  const cfg = d <= -8 ? { label: t("card.greatDeal"), color: "#16A34A", bg: "rgba(34,197,94,0.14)", Icon: Tag }
    : d <= 3 ? { label: t("card.goodPrice"), color: "#65A30D", bg: "rgba(132,204,22,0.18)", Icon: Tag }
      : d <= 8 ? { label: t("card.fairPrice"), color: "#D97706", bg: "rgba(245,158,11,0.18)", Icon: Tag }
        : { label: t("card.aboveMarket"), color: "#E11D48", bg: "rgba(240,67,106,0.14)", Icon: TrendingUp };
  const deltaTxt = d < 0 ? `${Math.abs(d)}% ${t("d.belowMarket")}` : d > 0 ? `${d}% ${t("d.aboveMarket")}` : t("d.atMarket");
  return (
    <div data-testid="price-vs-market-badge" className="mt-3 flex w-fit items-center gap-2 rounded-full px-3 py-1.5 text-sm font-bold"
      style={{ color: cfg.color, background: cfg.bg }}>
      <cfg.Icon size={15} /> {cfg.label}
      <span className="font-semibold opacity-80">· {deltaTxt}</span>
    </div>
  );
}

export default function MarketComparison({ carId, fallbackDeal, t }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [history, setHistory] = useState(null);
  const [range, setRange] = useState(90);
  const [markets, setMarkets] = useState([]);
  const [country, setCountry] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  // Available markets (seller's country first) for the switcher menu.
  useEffect(() => {
    let alive = true;
    crm(`/market/markets/${carId}`)
      .then((d) => { if (alive) setMarkets(d.markets || []); })
      .catch(() => {});
    return () => { alive = false; };
  }, [carId]);

  useEffect(() => {
    const onClick = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const q = country ? `?country=${country}` : "";
    crm(`/market/compare/${carId}${q}`)
      .then((d) => { if (alive) { setData(d); setLoading(false); } })
      .catch(() => { if (alive) { setData({ has_data: false }); setLoading(false); } });
    return () => { alive = false; };
  }, [carId, country]);

  useEffect(() => {
    let alive = true;
    crm(`/market/history/${carId}?days=${range}`)
      .then((d) => { if (alive) setHistory(d); })
      .catch(() => { if (alive) setHistory({ has_data: false }); });
    return () => { alive = false; };
  }, [carId, range]);

  if (loading) {
    return (
      <div data-testid="market-comparison-loading" className="mt-4 flex items-center gap-2 rounded-2xl border border-white/10 bg-white/[0.04] p-4 text-sm text-white/60">
        <Loader2 size={16} className="animate-spin text-pactly-blue" /> Analyse du marché en cours…
      </div>
    );
  }

  // No PMI data → fall back to the legacy external deal-score (never show both).
  if (!data?.has_data) {
    // Pool apparié sous le minimum → honnêteté : aucun %, aucun badge.
    if (data?.insufficient) {
      const c = data.criteria || {};
      const km = c.km_min != null && c.km_max != null
        ? ` ${c.km_min.toLocaleString("fr-BE")}–${c.km_max.toLocaleString("fr-BE")} km` : "";
      const tried = (data.markets_tried || []).filter((x) => x.pool_size);
      return (
        <div data-testid="market-insufficient" className="mt-4 rounded-2xl border border-white/10 bg-white/[0.04] p-5 text-sm">
          <div className="flex items-start gap-2 text-white/80">
            <Info size={16} className="mt-0.5 shrink-0 text-pactly-blue" />
            <div>
              <div className="font-bold text-white">Données insuffisantes pour estimer ce véhicule</div>
              <div className="mt-1 text-white/60">
                Moins de {data.min_required || 8} annonces réellement comparables
                (années {c.year_min}-{c.year_max}{km}). Aucun pourcentage affiché tant que l'estimation n'est pas fiable.
              </div>
              {tried.length > 0 && (
                <ul className="mt-2 space-y-0.5 text-[12px] text-white/55">
                  {tried.map((x) => (
                    <li key={x.country} data-testid={`market-tried-${x.country}`}>
                      {x.country} : {x.pool_size} annonce{x.pool_size > 1 ? "s" : ""},
                      dont {x.comparable_count} comparable{x.comparable_count > 1 ? "s" : ""} — insuffisant
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      );
    }
    if (fallbackDeal && fallbackDeal.available !== false && fallbackDeal.delta_pct != null && t) {
      return <LegacyBadge deal={fallbackDeal} t={t} />;
    }
    return null;
  }

  const m = data.market || {};
  const tone = TONE[data.assessment?.tone] || TONE.amber;
  const delta = data.delta_pct;
  const deltaTxt = `${delta > 0 ? "+" : ""}${delta}%`;
  const low = m.low, high = m.high;
  const carPct = pct(data.car_price, low, high);
  const p25Pct = pct(m.p25, low, high);
  const p75Pct = pct(m.p75, low, high);
  const medPct = pct(m.median, low, high);
  const activeMarket = markets.find((x) => x.code === (country || data.market_country)) || markets[0];

  return (
    <div data-testid="market-comparison" className="mt-4 rounded-2xl border border-white/10 bg-white/[0.04] p-5">
      {/* Market selector — seller's country by default, switchable */}
      {markets.length > 1 && (
        <div ref={menuRef} className="relative mb-4">
          <button data-testid="market-country-toggle" onClick={() => setMenuOpen((o) => !o)}
            className="flex w-full items-center gap-2 rounded-xl border border-white/10 bg-white/[0.04] px-3 py-2 text-left transition hover:bg-white/[0.07]">
            <span className="text-base" aria-hidden>{activeMarket?.flag}</span>
            <span className="text-sm font-bold text-white">{activeMarket?.label}</span>
            {activeMarket?.is_seller && <span className="rounded-full bg-[#7FB2E8]/15 px-2 py-0.5 text-[10px] font-bold text-[#7FB2E8]">Pays du vendeur</span>}
            <ChevronDown size={16} className={`ml-auto text-white/50 transition ${menuOpen ? "rotate-180" : ""}`} />
          </button>
          {menuOpen && (
            <div data-testid="market-country-menu" className="absolute z-30 mt-1 w-full overflow-hidden rounded-xl border border-white/10 bg-[#0F2540] shadow-2xl">
              {markets.map((mk) => (
                <button key={mk.code} data-testid={`market-country-${mk.code}`}
                  onClick={() => { setCountry(mk.code); setMenuOpen(false); }}
                  className="flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm transition hover:bg-white/[0.06]">
                  <span className="text-base" aria-hidden>{mk.flag}</span>
                  <span className="font-medium text-white/90">{mk.label}</span>
                  {mk.is_seller && <span className="rounded-full bg-[#7FB2E8]/15 px-1.5 py-0.5 text-[9px] font-bold text-[#7FB2E8]">Vendeur</span>}
                  {(country || data.market_country) === mk.code && <Check size={15} className="ml-auto text-emerald-400" />}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="text-xl" aria-hidden>{data.assessment?.icon}</span>
          <span className="font-display text-base font-extrabold" style={{ color: tone.text }}>{data.assessment?.label}</span>
        </div>
        <span data-testid="market-delta-badge" className="rounded-full px-3 py-1 text-sm font-extrabold" style={{ color: tone.text, background: tone.bg }}>
          {deltaTxt} <span className="font-semibold opacity-70">vs marché</span>
        </span>
      </div>

      {/* Composition du pool apparié (transparence) */}
      <div data-testid="market-pool-composition" className="mt-2 text-[12px] leading-snug text-white/55">
        {data.pool_size} annonce{data.pool_size > 1 ? "s" : ""} sur le marché {data.market_label || data.market_country}
        {typeof data.comparable_count === "number" && (
          <>, dont <span className="font-semibold text-white/75">{data.comparable_count} réellement comparable{data.comparable_count > 1 ? "s" : ""}</span></>
        )}
        {data.criteria && (
          <> ({data.criteria.year_min}-{data.criteria.year_max}
            {data.criteria.km_min != null && data.criteria.km_max != null
              ? `, ${data.criteria.km_min.toLocaleString("fr-BE")}-${data.criteria.km_max.toLocaleString("fr-BE")} km` : ""})</>
        )}
        {data.fallback_from && (
          <span data-testid="market-fallback-note"> · marché du vendeur ({data.fallback_from}) insuffisant → comparaison sur {data.market_label || data.market_country}</span>
        )}
      </div>

      {data.near_new && (
        <div data-testid="market-near-new-note" className="mt-3 flex items-start gap-2 rounded-xl border border-amber-400/25 bg-amber-400/[0.08] px-3 py-2 text-[12px] leading-snug text-amber-200/90">
          <Info size={14} className="mt-0.5 shrink-0" />
          <span>Véhicule quasi neuf : la médiane inclut tous les kilométrages, cette comparaison reste donc <span className="font-semibold">indicative</span>.</span>
        </div>
      )}

      {/* Gauge */}
      <div className="mt-6">
        <div className="relative h-3 rounded-full" style={{ background: "linear-gradient(90deg,#16A34A 0%,#F59E0B 55%,#E11D48 100%)" }}>
          {/* P25–P75 highlighted band */}
          <div className="absolute top-0 h-full rounded-full border border-white/40 bg-white/15"
            style={{ left: `${p25Pct}%`, width: `${Math.max(2, p75Pct - p25Pct)}%` }} data-testid="market-iqr-band" />
          {/* Median marker */}
          <div className="absolute top-[-4px] h-[20px] w-[2px] bg-white/90" style={{ left: `${medPct}%` }} title={`Médiane ${formatPrice(m.median)}`} />
          {/* Car price dot */}
          <div className="absolute -top-1.5 flex -translate-x-1/2 flex-col items-center" style={{ left: `${carPct}%` }} data-testid="market-car-dot">
            <div className="h-6 w-6 rounded-full border-2 border-white shadow-lg" style={{ background: tone.dot, boxShadow: `0 0 0 4px ${tone.ring}` }} />
          </div>
        </div>
        {/* "Votre prix" label under the dot */}
        <div className="relative mt-2 h-4">
          <span className="absolute -translate-x-1/2 whitespace-nowrap text-[11px] font-bold" style={{ left: `${carPct}%`, color: tone.text }}>
            Votre prix
          </span>
        </div>
        {/* Range labels */}
        <div className="mt-1 flex items-center justify-between text-xs text-white/55">
          <span>{formatPrice(low)}</span>
          <span className="font-bold text-white/80">Médiane {formatPrice(m.median)}</span>
          <span>{formatPrice(high)}</span>
        </div>
      </div>

      {/* Price history (30/90 days) */}
      {history?.has_data && (
        <div className="mt-4 border-t border-white/8 pt-3" data-testid="market-history">
          <div className="mb-1 flex items-center justify-between">
            <div className="flex items-center gap-1.5 text-[11px] font-semibold text-white/60">
              {history.change_pct <= 0 ? <TrendingDown size={13} className="text-emerald-400" /> : <TrendingUp size={13} className="text-rose-400" />}
              Médiane marché
              <span className={history.change_pct <= 0 ? "text-emerald-400" : "text-rose-400"}>
                {history.change_pct > 0 ? "+" : ""}{history.change_pct}% / {range}j
              </span>
            </div>
            <div className="flex gap-1">
              {[30, 90].map((r) => (
                <button key={r} data-testid={`market-history-range-${r}`} onClick={() => setRange(r)}
                  className={`rounded px-2 py-0.5 text-[10px] font-bold transition ${range === r ? "bg-white/15 text-white" : "text-white/40 hover:text-white/70"}`}>
                  {r}j
                </button>
              ))}
            </div>
          </div>
          <div className="h-16 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={history.series} margin={{ top: 4, right: 0, bottom: 0, left: 0 }}>
                <defs>
                  <linearGradient id="mktHist" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#7FB2E8" stopOpacity={0.5} />
                    <stop offset="100%" stopColor="#7FB2E8" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <YAxis hide domain={["dataMin", "dataMax"]} />
                <Tooltip
                  contentStyle={{ background: "#0F2540", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, fontSize: 12 }}
                  labelStyle={{ color: "#9CB3CC" }} formatter={(v) => [formatPrice(v), "Médiane"]} />
                <Area type="monotone" dataKey="median" stroke="#7FB2E8" strokeWidth={2} fill="url(#mktHist)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Sources footer (anonymized) */}
      <div className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-white/8 pt-3 text-[11px] text-white/50">
        {(data.sources || []).map((s, i) => (
          <span key={i} className="inline-flex items-center gap-1" data-testid={`market-source-${i}`}>
            <span aria-hidden>{s.flag}</span> {s.label} ({s.sample_size} annonces)
          </span>
        ))}
        {data.reliability !== "high" && (
          <span className="inline-flex items-center gap-1 font-semibold text-amber-400" data-testid="market-low-reliability">
            ⚠️ Données limitées
          </span>
        )}
      </div>
    </div>
  );
}
